import { ReactElement, useMemo, useRef, useState } from 'react';
import styles from './task-team-info.module.scss';
import listStyles from './task-info-item.module.scss';
import {
    UIButton,
    UIErrorState,
    UILoadingState,
    UIMenu,
    UIMenuItem,
    UIPopover,
    UIPropoverRef,
    UIText
} from '@/ui';
import { blue, opacity, yellow } from '@/ui/styles';
import { TaskInfoItem, TaskInfoItemProps } from './task-info-item';
import { useTaskInfo } from '../providers';
import { Contact, GetTaskInfoResponseData, Procedure } from '@/api';
import { parsePhoneNumberFromString } from 'libphonenumber-js';
import { isCurrentlyOutOfHours } from '@/companies/util/team-time.helper';
import { useTasksTranslation } from '../hooks';

export function TaskTeamInfo(): ReactElement {
    const { t } = useTasksTranslation();
    const { taskInfo, status, refetch } = useTaskInfo();

    return (
        <div className={styles.teamInfoContainer}>
            {status === 'pending' && <UILoadingState />}
            {status === 'error' && (
                <UIErrorState
                    title={t('shared.errors.load_data')}
                    onRetry={refetch}
                />
            )}
            {status === 'success' && taskInfo && (
                <LoadedContent taskInfo={taskInfo} />
            )}
        </div>
    );
}
function LoadedContent(props: {
    taskInfo: GetTaskInfoResponseData;
}): ReactElement {
    const { taskInfo } = props;
    const { t } = useTasksTranslation();
    const ref = useRef<UIPropoverRef>(null);

    const [selectedIndex, setSelectedIndex] = useState(0);

    // An explicit default: the switch used to fall off the end and hand the
    // button `undefined` for any index but 0 or 1.
    const buttonText = useMemo(() => {
        switch (selectedIndex) {
            case 1:
                return t('components.task_team_info.tab_procedures');
            case 0:
            default:
                return t('components.task_team_info.tab_contacts');
        }
    }, [selectedIndex, t]);

    function onClick(index: number) {
        setSelectedIndex(index);
        ref.current?.close();
    }

    const [activeContacts, isOutOfHours] = useMemo(() => {
        if (
            isCurrentlyOutOfHours(
                taskInfo.teamContactsSchedule,
                taskInfo.teamOutOfHoursEnabled,
                taskInfo.teamOutOfHoursContacts
            )
        ) {
            return [taskInfo.teamOutOfHoursContacts, true];
        } else {
            return [taskInfo.teamContacts, false];
        }
    }, [
        taskInfo.teamContacts,
        taskInfo.teamContactsSchedule,
        taskInfo.teamOutOfHoursEnabled,
        taskInfo.teamOutOfHoursContacts
    ]);

    return (
        <div className={styles.teamInfoContainer}>
            <div className={styles.sectionHeaderWrapper}>
                <UIPopover
                    ref={ref}
                    placement="bottom_right"
                    popoverPadding={4}
                    trigger="click"
                    triggerNode={
                        <button className={styles.removeButtonStyle}>
                            <UIButton
                                variant="textPrimary"
                                rightIcon="chevron-down"
                                withoutButtonHtmlTag
                                customPadding={8}
                            >
                                {buttonText}
                            </UIButton>
                        </button>
                    }
                >
                    <UIMenu>
                        <UIMenuItem
                            text={t('components.task_team_info.tab_contacts')}
                            onClick={() => {
                                onClick(0);
                            }}
                            leftIconName={
                                selectedIndex === 0 ? 'check' : undefined
                            }
                            leftIconColor={blue}
                        />
                        <UIMenuItem
                            text={t('components.task_team_info.tab_procedures')}
                            onClick={() => {
                                onClick(1);
                            }}
                            leftIconName={
                                selectedIndex === 1 ? 'check' : undefined
                            }
                            leftIconColor={blue}
                        />
                    </UIMenu>
                </UIPopover>
            </div>
            {selectedIndex === 0 && (
                <TaskContactsList
                    teamContacts={activeContacts}
                    isOutOfHours={isOutOfHours}
                />
            )}
            {selectedIndex === 1 && (
                <TaskProceduresList teamProcedures={taskInfo.teamProcedures} />
            )}
        </div>
    );
}

// function minutesToTimeString(minutes: number): string {
//     const hours = Math.floor(minutes / 60);
//     const mins = minutes % 60;
//     return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;
// }

function TaskContactsList(props: {
    teamContacts: Contact[];
    isOutOfHours: boolean;
}): ReactElement {
    const { teamContacts, isOutOfHours } = props;
    const { t } = useTasksTranslation();

    const items: TaskInfoItemProps[] = useMemo(() => {
        return teamContacts.map((contact, index) => {
            const phone = parsePhoneNumberFromString(contact.phone);
            return {
                title: t('shared.tasks.contact_name_with_role', {
                    name: contact.name,
                    role: contact.role
                }),
                phone: phone?.formatInternational(),
                listNumber: index + 1
            };
        });
    }, [teamContacts, t]);

    return (
        <div className={listStyles.container}>
            <div className={listStyles.listContainer}>
                {isOutOfHours && (
                    <div
                        style={{
                            marginLeft: 16,
                            marginRight: 16,
                            paddingLeft: 16,
                            paddingRight: 16,
                            paddingTop: 16,
                            paddingBottom: 16,
                            borderRadius: 8,
                            backgroundColor: yellow + opacity(0.4),
                            display: 'flex',
                            alignItems: 'center'
                        }}
                    >
                        <UIText variant="small" type="default2">
                            {t('components.task_contacts_list.out_of_hours')}
                            {/* {' ('}
                            <UIText variant="small" type="default2">
                                {minutesToTimeString(outOfHoursStartTime)}
                            </UIText>
                            {' - '}
                            <UIText variant="small" type="default2">
                                {minutesToTimeString(outOfHoursEndTime)}
                            </UIText>
                            {')'} */}
                        </UIText>
                    </div>
                )}
                {items.map((props, index) => (
                    <TaskInfoItem key={index} {...props} />
                ))}
                {items.length === 0 && (
                    <UIErrorState
                        title={t('components.task_contacts_list.empty')}
                    />
                )}
            </div>
        </div>
    );
}

function TaskProceduresList(props: {
    teamProcedures: Procedure[];
}): ReactElement {
    const { teamProcedures } = props;
    const { t } = useTasksTranslation();

    const items: TaskInfoItemProps[] = useMemo(() => {
        return teamProcedures.map((procedure, index) => {
            return {
                title: t('components.task_procedures_list.item_title', {
                    number: index + 1
                }),
                text: procedure.text,
                listNumber: index + 1
            };
        });
    }, [teamProcedures, t]);

    return (
        <div className={listStyles.container}>
            <div className={listStyles.listContainer}>
                {items.map((props, index) => (
                    <TaskInfoItem key={index} {...props} />
                ))}
                {items.length === 0 && (
                    <UIErrorState
                        title={t('components.task_procedures_list.empty')}
                    />
                )}
            </div>
        </div>
    );
}
